import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { BsCheckCircleFill, BsCloudUpload, BsImage, BsLink45Deg, BsPencil, BsTrash, BsX } from 'react-icons/bs';
import { discardSetImage, uploadSetImage } from '../api/setsApi';
import { imageUrl } from '../utils/setModel';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_SIZE = 5 * 1024 * 1024;

export default function ManagedImageField({ fieldDef, value, onChange, onPresetSelect, setId, onAssetUploaded }) {
  const { options = [], type } = fieldDef;
  const inputRef = useRef(null);
  const [expanded, setExpanded] = useState(!imageUrl(value));
  const [mode, setMode] = useState(type === 'image-selector' && options.length ? 'presets' : 'upload');
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [urlDraft, setUrlDraft] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const currentUrl = imageUrl(value);
  const presets = options.filter((option) => option.key !== 'custom');
  const selectedPreset = presets.find((option) => option.url === currentUrl);
  const sourceLabel = value && typeof value === 'object' && value.assetId ? 'Uploaded image' : selectedPreset ? selectedPreset.label : currentUrl ? 'Image URL' : 'No image';
  const emptyHint = fieldDef.emptyHint || (fieldDef.isBackground ? 'Optional — use a color instead' : 'Choose a preset, upload a file, or use an image URL');

  useEffect(() => setPreviewFailed(false), [currentUrl]);
  const modes = useMemo(() => [
    ...(presets.length ? [{ key: 'presets', label: 'Presets' }] : []),
    { key: 'upload', label: 'Upload' },
    { key: 'url', label: 'URL' },
  ], [presets.length]);

  const discardPendingValue = async () => {
    if (value && typeof value === 'object' && value.assetId && setId) await discardSetImage(setId, value.assetId).catch(() => undefined);
  };
  const choose = async (nextValue, collapse = true) => {
    await discardPendingValue();
    onChange(nextValue);
    setError('');
    if (collapse && nextValue) setExpanded(false);
  };
  const processFile = async (file) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) return setError('Choose a JPG, PNG, GIF, or WebP image.');
    if (file.size > MAX_SIZE) return setError('This file is larger than 5 MB.');
    if (!setId) return setError('Save or load the set before uploading images.');
    setBusy(true); setError('');
    try {
      const asset = await uploadSetImage(setId, file);
      await discardPendingValue();
      onAssetUploaded?.(asset.assetId);
      onChange(asset);
      setExpanded(false);
    } catch (uploadError) { setError(uploadError.message); }
    finally { setBusy(false); }
  };
  const fileChanged = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    processFile(file);
  };
  const dropped = (event) => {
    event.preventDefault(); setDragging(false);
    if (!busy) processFile(event.dataTransfer.files?.[0]);
  };
  const applyUrl = () => {
    let parsed;
    try { parsed = new URL(urlDraft); } catch { setError('Enter a complete image URL, including https://'); return; }
    if (!['http:', 'https:'].includes(parsed.protocol)) return setError('Only http:// and https:// URLs are supported.');
    choose(parsed.toString()); setUrlDraft('');
  };

  return (
    <div className={`set-image-field ${expanded ? 'set-is-expanded' : 'set-is-compact'}`}>
      <div className="set-image-summary">
        <div className="set-image-summary-thumb">{currentUrl && !previewFailed ? <img src={currentUrl} alt="" onError={() => setPreviewFailed(true)} /> : <BsImage />}</div>
        <div className="set-image-summary-copy"><strong>{sourceLabel}</strong><span>{currentUrl ? 'Image selected' : emptyHint}</span></div>
        <div className="set-image-summary-actions">
          <Button size="sm" variant="outline-primary" onClick={() => setExpanded((open) => !open)} disabled={busy}>{expanded ? <BsX /> : <BsPencil />}<span>{expanded ? 'Close' : currentUrl ? 'Change' : 'Choose'}</span></Button>
          {currentUrl && <Button size="sm" variant="outline-danger" onClick={() => choose('', false)} disabled={busy} aria-label="Remove image"><BsTrash /></Button>}
        </div>
      </div>

      {expanded && <div className="set-image-picker-panel">
        <div className="set-image-source-tabs" role="tablist" aria-label="Image source">{modes.map((item) => <button key={item.key} type="button" role="tab" aria-selected={mode === item.key} className={mode === item.key ? 'set-is-active' : ''} onClick={() => { setMode(item.key); setError(''); }}>{item.label}</button>)}</div>
        {mode === 'presets' && <div className="set-image-presets" role="listbox" aria-label={fieldDef.title}>{presets.map((option) => <button key={option.key || option.url} type="button" className={`set-image-preset ${currentUrl === option.url ? 'set-is-selected' : ''}`} onClick={() => { onPresetSelect?.(option); choose(option.url); }} aria-label={option.label} aria-selected={currentUrl === option.url}><span className="set-image-preset-thumb"><img src={option.url} alt="" loading="lazy" />{currentUrl === option.url && <BsCheckCircleFill />}</span><span>{option.label}</span></button>)}</div>}
        {mode === 'upload' && <div className={`set-image-dropzone ${dragging ? 'set-is-dragging' : ''} ${busy ? 'set-is-busy' : ''}`} onDragEnter={(event) => { event.preventDefault(); setDragging(true); }} onDragOver={(event) => event.preventDefault()} onDragLeave={() => setDragging(false)} onDrop={dropped} onClick={() => !busy && inputRef.current?.click()} role="button" tabIndex={0} onKeyDown={(event) => { if ((event.key === 'Enter' || event.key === ' ') && !busy) inputRef.current?.click(); }}><input ref={inputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={fileChanged} />{busy ? <Spinner animation="border" size="sm" /> : <BsCloudUpload />}<div><strong>{busy ? 'Uploading…' : 'Drop or choose a file'}</strong><span>JPG, PNG, GIF or WebP · max 5 MB</span></div></div>}
        {mode === 'url' && <div className="set-image-url-panel"><InputGroup size="sm"><InputGroup.Text><BsLink45Deg /></InputGroup.Text><Form.Control value={urlDraft} onChange={(event) => setUrlDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); applyUrl(); } }} placeholder="https://example.com/image.webp" /><Button variant="primary" onClick={applyUrl} disabled={!urlDraft.trim() || busy}>Use</Button></InputGroup></div>}
        {error && <Alert variant="danger" className="set-image-error">{error}</Alert>}
      </div>}
    </div>
  );
}
